import {
  HttpClient,
  HttpEventType,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  publish,
  refCount,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { PresignedPostCreateGQL } from '../../modules/graphql/service/graphql-auto-main-service';
import { UploadStatus } from './enums/upload-status.enum';
import { PresignedData } from './types/presigned-data.type';
import { UploadItemProgressInternal } from './types/upload-item-progress-internal.type';
import { UploadItem } from './types/upload-item.type';

@Injectable()
export class UploadingService {
  constructor(
    private readonly http: HttpClient,
    private readonly presignedPostCreateGQL: PresignedPostCreateGQL,
  ) {}

  uploadFile(file: File, overrideName?: string): UploadItem {
    const progress: UploadItemProgressInternal = {
      status$: new BehaviorSubject(UploadStatus.initial),
      uploadingProgress$: new BehaviorSubject(0),
    };

    const fileKey$ = of({ file, overrideName }).pipe(
      tap(() => progress.status$.next(UploadStatus.signing)),
      switchMap(({ file, overrideName }) => this.fetchPresignedData(file, overrideName)),
      tap(() => progress.status$.next(UploadStatus.uploading)),
      switchMap((presignedData) =>
        this.uploadToStorage(presignedData, progress.uploadingProgress$),
      ),
      tap(() => progress.status$.next(UploadStatus.success)),
      catchError((err) => {
        progress.status$.next(UploadStatus.error);
        throw err;
      }),
      finalize(() => {
        progress.uploadingProgress$.complete();
        progress.status$.complete();
      }),
      publish(),
      refCount(),
      shareReplay({ bufferSize: 1, refCount: true }),
    ) as Observable<string>;

    return this.buildUploadItem(file, overrideName, fileKey$, progress);
  }

  private fetchPresignedData(
    file: File,
    overrideName: string | undefined,
  ): Observable<PresignedData> {
    return this.presignedPostCreateGQL
      .mutate(
        {
          input: {
            filename: overrideName || file.name,
            contentType: file.type,
          },
        },
        { fetchPolicy: 'no-cache' },
      )
      .pipe(
        map((res) => res.data?.presignedPostCreate),
        map((data) => {
          const fields = JSON.parse(data.fields);
          return {
            file,
            key: fields['key'],
            presignedUrl: data.url,
            fields,
          } as PresignedData;
        }),
      );
  }

  private uploadToStorage(
    { fields, file, key, presignedUrl }: PresignedData,
    uploadingProgress$: BehaviorSubject<number>,
  ): Observable<string> {
    const formData = new FormData();

    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file);

    const req = new HttpRequest('POST', presignedUrl, formData, {
      headers: new HttpHeaders({ enctype: 'multipart/form-data', accept: 'application/json' }),
      reportProgress: true,
    });

    return this.http.request(req).pipe(
      tap((event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          const progressValue = Math.round((100 * event.loaded) / event.total);
          uploadingProgress$.next(progressValue);
        }
      }),
      filter((event) => event instanceof HttpResponse),
      map(() => key),
    );
  }

  private buildUploadItem(
    file: File,
    overrideName: string | undefined,
    fileKey$: Observable<string>,
    progress: UploadItemProgressInternal,
  ): UploadItem {
    const filename = overrideName || file.name;

    return {
      file,
      filename,
      contentType: file.type,
      key$: fileKey$,
      loading$: fileKey$.pipe(
        map(() => false),
        startWith(true),
        catchError(() => of(false)),
        shareReplay({ bufferSize: 1, refCount: true }),
      ),
      status$: progress.status$.asObservable().pipe(shareReplay(1)),
      uploadingProgress$: progress.uploadingProgress$.asObservable().pipe(shareReplay(1)),
    };
  }
}
